import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { QuestionMark } from '../../components/widgets';
import ReferenceRow from './ReferenceRow';
import { TRANSLATION_PREFIX } from '../consts';
import css from '../page.css';

const SCROLL_THRESHOLD_PX = 200;

function ReferenceGrid({
    rows,
    visibleColumns,
    dictionaries,
    dictionaryOptions,
    perRowDictionaries,
    isValueCurrencyEnabled,
    valueTooltip,
    errorsByKey,
    filters,
    rowLimit,
    disabled,
    deleteDisabled,
    onFilterChange,
    onLoadMore,
    onCellChange,
    onDateChange,
    onValueChange,
    onDeleteRow,
}) {
    const sentinelRef = useRef(null);

    // Reveal more rows as the bottom of the list approaches the viewport (infinite scroll parity).
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || rows.length <= rowLimit) {
            return undefined;
        }
        const observer = new IntersectionObserver(
            entries => {
                if (entries.some(entry => entry.isIntersecting)) {
                    onLoadMore();
                }
            },
            { rootMargin: `${SCROLL_THRESHOLD_PX}px` }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [rows.length, rowLimit, onLoadMore]);

    const visibleRows = rows.slice(0, rowLimit);

    return (
        <div className={css.gridContainer}>
            <table className={css.referenceTable}>
                <thead>
                    <tr>
                        <th>
                            <span className={css.headerCell}>
                                <Translate id={`${TRANSLATION_PREFIX}.RULE_START_DATE`} />
                                <QuestionMark message={`${TRANSLATION_PREFIX}.LEAVE_BLANK_FOR_ALWAYS`} />
                            </span>
                        </th>
                        {visibleColumns.map(column => (
                            <th key={column.field}>
                                {/* Only dimension headers resize: they hold the long values. The
                                    resizable block stays the sole child so its grip sits at the
                                    column edge; a tooltip goes beside the label like every other
                                    header, and `overflow: hidden` cannot clip the popup because
                                    tippy appends it to document.body. */}
                                <div className={css.resizableHeader}>
                                    <span className={css.headerCell}>
                                        <Translate id={column.label} />
                                        {column.tooltip && <QuestionMark message={column.tooltip} />}
                                    </span>
                                </div>
                            </th>
                        ))}
                        <th>
                            <span className={css.headerCell}>
                                <Translate id={`${TRANSLATION_PREFIX}.VALUE`} />
                                {valueTooltip && <QuestionMark message={valueTooltip} />}
                            </span>
                        </th>
                        {isValueCurrencyEnabled && (
                            <th>
                                <span className={css.headerCell}>
                                    <Translate id={`${TRANSLATION_PREFIX}.VALUE_CURRENCY`} />
                                </span>
                            </th>
                        )}
                        <th className={css.actionColumn} aria-hidden="true" />
                    </tr>
                    <tr className={css.filterRow}>
                        <td />
                        {visibleColumns.map(column => (
                            <td key={column.field}>
                                <input
                                    className={css.filterInput}
                                    type="text"
                                    aria-label={`Filter ${column.field}`}
                                    value={filters[column.field] || ''}
                                    onChange={event => onFilterChange(column.field, event.target.value)}
                                />
                            </td>
                        ))}
                        <td />
                        {isValueCurrencyEnabled && <td />}
                        <td className={css.actionColumn} />
                    </tr>
                </thead>
                <tbody>
                    {visibleRows.map(row => (
                        <ReferenceRow
                            key={row._key}
                            row={row}
                            visibleColumns={visibleColumns}
                            dictionaries={dictionaries}
                            dictionaryOptions={dictionaryOptions}
                            perRowDictionaries={perRowDictionaries}
                            isValueCurrencyEnabled={isValueCurrencyEnabled}
                            errors={errorsByKey[row._key]}
                            disabled={disabled}
                            deleteDisabled={deleteDisabled}
                            onCellChange={(field, option, dictionary) => onCellChange(row._key, field, option, dictionary)}
                            onDateChange={startDate => onDateChange(row._key, startDate)}
                            onValueChange={value => onValueChange(row._key, value)}
                            onDelete={() => onDeleteRow(row._key)}
                        />
                    ))}
                </tbody>
            </table>
            <div ref={sentinelRef} className={css.scrollSentinel} />
        </div>
    );
}

ReferenceGrid.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    visibleColumns: PropTypes.arrayOf(PropTypes.object).isRequired,
    // Per-row dictionaries are keyed maps, so an entry is either shape.
    dictionaries: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
    dictionaryOptions: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
    perRowDictionaries: PropTypes.shape({
        loading: PropTypes.objectOf(PropTypes.bool).isRequired,
        onOpen: PropTypes.func.isRequired,
        onSearch: PropTypes.func.isRequired,
    }).isRequired,
    isValueCurrencyEnabled: PropTypes.bool,
    valueTooltip: PropTypes.string,
    errorsByKey: PropTypes.objectOf(PropTypes.object).isRequired,
    filters: PropTypes.objectOf(PropTypes.string).isRequired,
    rowLimit: PropTypes.number.isRequired,
    disabled: PropTypes.bool,
    deleteDisabled: PropTypes.bool,
    onFilterChange: PropTypes.func.isRequired,
    onLoadMore: PropTypes.func.isRequired,
    onCellChange: PropTypes.func.isRequired,
    onDateChange: PropTypes.func.isRequired,
    onValueChange: PropTypes.func.isRequired,
    onDeleteRow: PropTypes.func.isRequired,
};

export default ReferenceGrid;
